import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';

import { adminItemKey } from './queries';

/**
 * Adding and removing one credit.
 *
 * **A credit is one (person, title, role) record**, and the route that writes it
 * is scoped to the *credited person* rather than to the title. That is
 * deliberate on the owning side: the provider fill is what *creates* credits,
 * and this is the only route that *corrects* what it created — a wrong role or
 * a wrong person — which is why it hangs off the page where a person's whole
 * body of work is a single indexed read.
 *
 * The consequence here is that a role chip toggles a **record**, not a flag: a
 * person holding two roles on one title holds two credits, and removing one
 * addresses it by its own id. The invalidation set carries the credited-person
 * register as well as the title, because the same record is a row on both.
 */

function useCreditInvalidation() {
  const queryClient = useQueryClient();

  return (contentItemId: string) => {
    void queryClient.invalidateQueries({ queryKey: adminItemKey(contentItemId) });
    void queryClient.invalidateQueries({ queryKey: key('content', 'creditProfiles') });
  };
}

export function useAddCredit() {
  const invalidate = useCreditInvalidation();

  return useMutation({
    mutationFn: ({
      creditProfileId,
      contentItemId,
      role,
    }: {
      creditProfileId: string;
      contentItemId: string;
      role: string;
    }) =>
      api.post<null>('content', `/admin/creditProfiles/${creditProfileId}/credits`, {
        contentItemId,
        role,
      }),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}

export function useRemoveCredit() {
  const invalidate = useCreditInvalidation();

  return useMutation({
    mutationFn: ({
      creditProfileId,
      creditId,
    }: {
      creditProfileId: string;
      creditId: string;
      contentItemId: string;
    }) => api.delete<null>('content', `/admin/creditProfiles/${creditProfileId}/credits/${creditId}`),
    onSuccess: (_data, variables) => invalidate(variables.contentItemId),
    retry: false,
  });
}
